import { PlusIcon } from '@phosphor-icons/react';
import { Formik } from 'components/form/formik';
import * as Yup from 'yup';

import { Button } from '../../../components/button/Button';
import { InputField } from '../../../components/input/InputField';
import { SimpleSelectField } from '../../../components/select/SimpleSelectField';
import { TextAreaField } from '../../../components/textarea/TextAreaField';
import { useMinimalGeneralSettings } from '../../../contexts/minimal-settings-context';
import {
  CustomerType,
  InvoiceDeliveryMethod,
  InvoiceFrequency,
  Country,
  Language,
  EmailType,
  OrderDocumentType,
  PaymentTerm,
} from 'src/types/enums';
import { useEmailParams } from '../../settings/models';
import { type CreateCustomerInput } from '../models';
import { COUNTRY_VALUES } from '../../../utils/address';
import { nullthrows } from '../../../utils/invariant';
import { LANGUAGE_VALUES } from '../../../utils/language';
import { CargoTypeComboboxField, cargoTypeToComboboxItem } from '../../order/cargoType/CargoTypeComboboxField';
import { useTranslation } from '../../../contexts/translation-context';
import { CUSTOMER_TYPE_VALUES, INVOICE_DELIVERY_METHOD_VALUES, INVOICE_FREQUENCY_VALUES } from '../select-values';
import {
  customerFieldsFromInvoiceChannels,
  defaultInvoiceChannelsForCountry,
  INVOICE_CHANNELS,
  type InvoiceChannel,
} from '../document-delivery';
import { AutocompletePostalcode } from '../../location/components/AutocompletePostalcode';
import { CheckboxField } from '../../../components/checkbox/CheckboxField';
import { InvoiceChannelsField } from '../pages/Customer/InvoiceChannelsField';
import { RequiredDocumentsCard } from '../pages/Customer/RequiredDocumentsCard';
import { VatRateComboboxField, vatRateToComboboxItem } from '../../order/vatRate/VatRateComboboxField';
import { TrailerTypes } from '../../order/pages/order/TrailerTypes';
import { parseNumberInput } from '../../../utils/number';
import { Card } from 'components/Card';
import { TemplateTextAreaField } from 'components/textarea/TemplateTextAreaField';
import { UserComboboxField } from 'src/app/user/components/UserCombobox';
import { defaultPaymentTerm, SelectPaymentTerm } from 'components/select/SelectPaymentTerm';

const createCustomerSchema = Yup.object().shape({
  name: Yup.string().min(1, 'Vereist').required('Vereist'),
  type: Yup.mixed().nullable().required('Vereist'),
  vatNumber: Yup.string().vatNumber().required('Vereist'),
  companyNumber: Yup.string().companyNumber().required('Vereist'),
  glnNumber: Yup.string(),
  street: Yup.string().min(1, 'Vereist').required('Vereist'),
  streetNumber: Yup.string(),
  city: Yup.string().min(1, 'Vereist').required('Vereist'),
  postalCode: Yup.string().min(1, 'Vereist').required('Vereist'),
  country: Yup.mixed().nullable().required('Vereist'),
  language: Yup.mixed().nullable().required('Vereist'),
  paymentTerm: Yup.object()
    .shape({
      code: Yup.string().required(),
      value: Yup.string().required(),
      days: Yup.number().required(),
    })
    .required('Vereist'),
  invoiceFrequency: Yup.mixed().nullable().required('Vereist'),
  invoiceDeliveryMethod: Yup.mixed().nullable().required('Vereist'),
  defaultCargoType: Yup.mixed().nullable().required('Vereist'),
  defaultVatRate: Yup.mixed().nullable().required('Vereist'),
  sendCMRWithInvoice: Yup.boolean().required('Vereist'),
  autoInvoicing: Yup.boolean().required('Vereist'),
  dieselSurchargeEnabled: Yup.boolean().required('Vereist'),
  hasCustomDieselSurcharge: Yup.boolean().required('Vereist'),
  dieselSurchargePercentage: Yup.string().matches(/^-?[0-9]+(\.[0-9]+)?$/, 'Ongeldig getal'),
  hasSelfBilling: Yup.boolean().required('Vereist'),
  requiresPrepayment: Yup.boolean().required('Vereist'),
  invoiceChannels: Yup.array()
    .of(Yup.string().oneOf([...INVOICE_CHANNELS]))
    .required(),
  onlySendInvoiceAttachment: Yup.boolean().required('Vereist'),
  mergeInvoicePdfs: Yup.boolean().required('Vereist'),
});

export interface ICreateCustomerFormProps {
  /** When passed, a cancel button is rendered next to the submit button. */
  onCancel?: () => void;
  /** Must never throw — the caller owns error handling and reporting. */
  onSubmit: (input: CreateCustomerInput) => Promise<void>;
  /** Prefills the name field, e.g. with what the user typed in a combobox. */
  initialName?: string;
}

export const CreateCustomerForm: React.FC<ICreateCustomerFormProps> = (props) => {
  const { onCancel, onSubmit, initialName = '' } = props;
  const { settings } = useMinimalGeneralSettings();
  const { lang } = useTranslation();

  const { data: _emailParams } = useEmailParams(EmailType.Cmr);
  const emailParams = _emailParams ?? [];

  const initialValues = {
    name: initialName,
    vatNumber: '',
    companyNumber: '',
    glnNumber: '',
    street: '',
    streetNumber: '',
    city: '',
    postalCode: '',
    country: COUNTRY_VALUES.find((v) => v.key === Country.Be)!,
    language: LANGUAGE_VALUES.find((v) => v.key === Language.Nl)!,
    invoiceFrequency: INVOICE_FREQUENCY_VALUES[0],
    invoiceDeliveryMethod: INVOICE_DELIVERY_METHOD_VALUES[0],
    internalNotes: '',
    externalNotes: '',
    paymentTerm: defaultPaymentTerm,
    type: CUSTOMER_TYPE_VALUES[0],
    defaultCargoType: settings.defaultCargoType ? cargoTypeToComboboxItem(settings.defaultCargoType, lang) : null,
    defaultVatRate: settings.defaultVatRate ? vatRateToComboboxItem(settings.defaultVatRate) : null,
    sendCMRWithInvoice: false,
    autoInvoicing: false,
    dieselSurchargeEnabled: false,
    hasCustomDieselSurcharge: false,
    defaultTrailerTypes: [],
    cmrReminderOverwrite: '',
    cmrEmailSubjectOverwrite: '',
    dieselSurchargePercentage: '0',
    hasSelfBilling: false,
    requiresPrepayment: false,
    invoiceChannels: defaultInvoiceChannelsForCountry(Country.Be),
    onlySendInvoiceAttachment: false,
    mergeInvoicePdfs: false,
    dealOwner: settings.defaultDealOwner ?? null,
    autoProcessCmr: true,
    // Matches the database default.
    requiredDocuments: [OrderDocumentType.Cmr],
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={createCustomerSchema}
      onSubmit={async (values) => {
        const { defaultCargoType, defaultVatRate, dealOwner, invoiceChannels, ...otherValues } = values;
        const inputData: CreateCustomerInput = {
          ...otherValues,
          ...customerFieldsFromInvoiceChannels(invoiceChannels),
          dealOwnerId: nullthrows(dealOwner?.id, 'Dealeigenaar is vereist'),
          country: values.country.key as Country,
          language: values.language.key as Language,
          paymentTerm: values.paymentTerm.code as PaymentTerm,
          invoiceFrequency: values.invoiceFrequency.key as InvoiceFrequency,
          invoiceDeliveryMethod: values.invoiceDeliveryMethod.key as InvoiceDeliveryMethod,
          type: values.type.key as CustomerType,
          defaultCargoTypeId: nullthrows(defaultCargoType?.key, 'Standaard cargo type is vereist'),
          defaultVatRateId: nullthrows(defaultVatRate?.key, 'Standaard btw type is vereist'),
          cmrReminderOverwrite: values.cmrReminderOverwrite ? +values.cmrReminderOverwrite : null,
          // The percentage only carries meaning for a customer with its own rate;
          // the guards below zero it out for every other combination.
          dieselSurchargePercentage:
            values.hasCustomDieselSurcharge && values.dieselSurchargePercentage
              ? parseNumberInput(values.dieselSurchargePercentage, 2)
              : 0,
        };

        if (!inputData.dieselSurchargeEnabled) {
          inputData.dieselSurchargePercentage = 0;
          inputData.hasCustomDieselSurcharge = false;
        }

        if (!inputData.hasCustomDieselSurcharge) {
          inputData.dieselSurchargePercentage = 0;
        }

        await onSubmit(inputData);
      }}
    >
      {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
        <form
          onSubmit={(evt) => {
            // This form can render inside a dialog that is portaled to <body>, but React
            // propagates `submit` over the fiber tree — without this, the page's own form
            // (e.g. the order form) would submit too.
            evt.stopPropagation();
            handleSubmit(evt);
          }}
          className="grid gap-4"
        >
          <Card title="Contactgegevens">
            <InputField labelText="Naam" type="text" name="name" />
            <UserComboboxField labelText="Dealeigenaar" name="dealOwner" />
            <SimpleSelectField labelText="Type klant" items={CUSTOMER_TYPE_VALUES} name="type" />
            <InputField labelText="BTW Nummer" type="text" name="vatNumber" />
            <InputField labelText="Ondernemingsnummer" type="text" name="companyNumber" />
            <InputField labelText="GLN Nummer" type="text" name="glnNumber" />
            <div className="flex gap-4">
              <div className="flex-4">
                <InputField labelText="Straat" type="text" name="street" />
              </div>
              <div className="flex-1">
                <InputField labelText="Nr" type="text" name="streetNumber" />
              </div>
            </div>
            <AutocompletePostalcode />
            <SimpleSelectField labelText="Taal" items={LANGUAGE_VALUES} name="language" />
          </Card>

          <Card title="Facturatie">
            <SelectPaymentTerm
              name="paymentTerm"
              value={values.paymentTerm}
              onChange={(value) => setFieldValue('paymentTerm', value)}
            />
            <SimpleSelectField
              labelText="Factuur frequentie"
              items={INVOICE_FREQUENCY_VALUES}
              name="invoiceFrequency"
            />
            <SimpleSelectField
              labelText="Factuur levering"
              items={INVOICE_DELIVERY_METHOD_VALUES}
              name="invoiceDeliveryMethod"
            />
            <InvoiceChannelsField
              country={values.country?.key}
              defaultChannelsForCountry={defaultInvoiceChannelsForCountry}
            />
            <TextAreaField labelText="Interne notities" name="internalNotes" spellCheck={true} />
            <TextAreaField labelText="Externe notities (voor vervoerder)" name="externalNotes" spellCheck={true} />

            <div className="grid grid-cols-2 gap-x-2">
              <CheckboxField labelText="Automatische facturatie" name="autoInvoicing" />
              <CheckboxField labelText="CMRs bij facturatie email" name="sendCMRWithInvoice" />
              <CheckboxField labelText="Self billing" name="hasSelfBilling" />
              <CheckboxField labelText="Geen extra bijlagen bij factuur e-mail" name="onlySendInvoiceAttachment" />
              <CheckboxField labelText="PDF's samenvoegen bij facturatie" name="mergeInvoicePdfs" />
              <CheckboxField labelText="Vooruitbetaling vereist" name="requiresPrepayment" />
            </div>
          </Card>

          <Card title="CMR">
            <InputField
              labelText="Dagen voor CMR Herinnering overwrite"
              type="number"
              name="cmrReminderOverwrite"
              step="1"
            />
            <TemplateTextAreaField
              labelText="CMR Email onderwerp"
              name="cmrEmailSubjectOverwrite"
              params={emailParams}
              rows={1}
            />
            <CheckboxField labelText="Automatische CMR verwerking" name="autoProcessCmr" />
          </Card>

          <RequiredDocumentsCard />

          <Card title="Diesel toeslag">
            <CheckboxField labelText="Gebruikt diesel toeslag" name="dieselSurchargeEnabled" />
            {values['dieselSurchargeEnabled'] && (
              <div>
                <CheckboxField
                  labelText="Eigen diesel toeslag"
                  name="hasCustomDieselSurcharge"
                  isDisabled={!values['dieselSurchargeEnabled']}
                />
                <InputField
                  labelText="Eigen diesel toeslag"
                  type="text"
                  name="dieselSurchargePercentage"
                  step="0.01"
                  isDisabled={!values['dieselSurchargeEnabled'] || !values['hasCustomDieselSurcharge']}
                />
              </div>
            )}
          </Card>

          <Card title="Standaard waarden">
            <CargoTypeComboboxField labelText="Standaard inhoud type" name="defaultCargoType" />
            <VatRateComboboxField labelText="Standaard BTW Tarief" name="defaultVatRate" />
          </Card>

          <Card title="Standaard Trailer types">
            <TrailerTypes
              value={values.defaultTrailerTypes}
              onChange={(newTrailerTypes) => {
                setFieldValue('defaultTrailerTypes', newTrailerTypes);
              }}
              isMultiSelect={true}
            />
          </Card>

          <div className="flex justify-between">
            {onCancel && (
              <Button type="button" color="secondary" isDisabled={isSubmitting} onTrigger={onCancel}>
                Annuleer
              </Button>
            )}

            <Button
              type="submit"
              color="primary"
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
              iconLeft={<PlusIcon className="button-icon" />}
            >
              Maak klant aan
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};
